import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import service from '../../services/cakeorders.service';
import PrintContainer from '../PrintContainer';
import ThermalReceipt, { ThermalReceiptRow, ThermalReceiptSection } from '../receipts/ThermalReceipt';
import storeLogo from '../../images/logo-los-portales.png';
import { cakeDifferences } from '../../helpers/cakes';
import { formatPhoneForDisplay } from '../../helpers/phone';
import { getOrderAuditLog } from '../../services/orderAudit.service';
import OrderAuditTimeline from '../shared/OrderAuditTimeline';
import cakeTableStyles from './CakeOrdersTable.module.css';

export default function CakeOrder({ cake }) {
    const contentRef = useRef();
    const [isEditing, setIsEditing] = useState(false);
    const [editedCake, setEditedCake] = useState(cake);
    const [auditLog, setAuditLog] = useState([]);
    const [auditVersion, setAuditVersion] = useState(0);

    useEffect(() => {
        getOrderAuditLog('cakes', cake.id).then(setAuditLog).catch(console.error);
    }, [cake.id, auditVersion]);

    function deleteCakeOrder() {
        if (window.confirm("Seguro que quieres borrar esta orden?")) {
            service.deleteCakeOrder(cake.id);
        }
    }

    function handleInputChange(e) {
        const { name, value } = e.target;
        setEditedCake(prevCake => ({ ...prevCake, [name]: value }));
    }

    async function saveChanges() {
        try {
            const differences = cakeDifferences(cake, editedCake);
            console.log('Differences:', differences);
           await service.editCakeOrder(cake.id, differences);
            setIsEditing(false);
            setAuditVersion(v => v + 1);
        } catch (error) {
            console.error('Error updating cake order:', error);
            alert('Error updating cake order. Please try again.');
        }
    }

    const renderInput = (key, value) => {
        switch (key) {
            case 'date':
                return <input type="date" name={key} value={value} onChange={handleInputChange} />;
            case 'time':
                return (
                    <select name={key} value={value} onChange={handleInputChange}>
                        <option value="2pm">2:00 PM</option>
                        <option value="3pm">3:00 PM</option>
                        <option value="4pm">4:00 PM</option>
                        <option value="5pm">5:00 PM</option>
                        <option value="6pm">6:00 PM</option>
                        <option value="7pm">7:00 PM</option>
                        <option value="8pm">8:00 PM</option>
                    </select>
                );
            case 'phoneNumber':
                return <input type="tel" name={key} value={value} onChange={handleInputChange} />;
            case 'image':
                return <input type="url" name={key} value={value} onChange={handleInputChange} />;
            default:
                return <input type="text" name={key} value={value} onChange={handleInputChange} />;
        }
    };

    const receiptData = {
        title: 'Pedido de Pastel',
        subtitle: `${editedCake.firstName} ${editedCake.lastName}`,
        meta: [
            { label: 'Fecha', value: editedCake.date },
            { label: 'Hora', value: editedCake.time },
        ],
        sections: [
            {
                title: 'Contacto',
                rows: [
                    { label: 'Numero', value: formatPhoneForDisplay(editedCake.phoneNumber) },
                ],
            },
            {
                title: 'Detalles del Pastel',
                rows: [
                    { label: 'Tamaño', value: editedCake.cakeSize },
                    { label: 'Relleno', value: editedCake.filling1 },
                    ...(editedCake.filling2 ? [{ label: 'Relleno 2', value: editedCake.filling2 }] : []),
                    { label: 'Imagen', value: editedCake.image },
                    { label: 'Dedicatoria', value: editedCake.message },
                    { label: 'Color', value: editedCake.color },
                ],
            },
            {
                title: 'Notas',
                rows: [
                    ...(editedCake.comments ? [{ label: 'Comentarios', value: editedCake.comments }] : []),
                    { label: 'Pagado', value: editedCake.prePaid ? 'Si' : 'No', emphasize: editedCake.prePaid },
                    { label: 'Cajer@', value: editedCake.cashier },
                ],
            },
        ],
        footerNote: 'Revisar pago antes de entregar',
    };

    return (
        <div>
            <PrintContainer contentRef={contentRef} receiptData={receiptData}>
                <ThermalReceipt
                    logoSrc={storeLogo}
                    logoAlt="Los Portales"
                    title="Cake Order"
                    subtitle={`${editedCake.firstName} ${editedCake.lastName}`}
                    meta={[
                        { label: 'Fecha', value: isEditing ? renderInput('date', editedCake.date) : editedCake.date },
                        { label: 'Hora', value: isEditing ? renderInput('time', editedCake.time) : editedCake.time }
                    ]}
                    footerNote="Revisar pago antes de entregar"
                >
                    <ThermalReceiptSection title="Contacto">
                        <ThermalReceiptRow
                            label="Numero"
                            value={isEditing ? renderInput('phoneNumber', editedCake.phoneNumber) : formatPhoneForDisplay(editedCake.phoneNumber)}
                        />
                    </ThermalReceiptSection>
                    <ThermalReceiptSection title="Detalles del Pastel">
                        <ThermalReceiptRow
                            label="Tamaño"
                            value={isEditing ? renderInput('cakeSize', editedCake.cakeSize) : editedCake.cakeSize}
                        />
                        <ThermalReceiptRow
                            label="Relleno"
                            value={isEditing ? renderInput('filling1', editedCake.filling1) : editedCake.filling1}
                        />
                        {(editedCake.filling2 || isEditing) && (
                            <ThermalReceiptRow
                                label="Relleno 2"
                                value={isEditing ? renderInput('filling2', editedCake.filling2) : editedCake.filling2}
                            />
                        )}
                        <ThermalReceiptRow
                            label="Imagen"
                            value={
                                isEditing ? renderInput('image', editedCake.image) : (
                                    <>
                                        {editedCake.image}
                                        {(editedCake.createdImageUrl || editedCake.imageUrl) && (
                                            <img src={editedCake.createdImageUrl || editedCake.imageUrl} alt="Cake" className={cakeTableStyles.cakeImage} />
                                        )}
                                    </>
                                )
                            }
                        />
                        <ThermalReceiptRow
                            label="Dedicatoria"
                            value={isEditing ? renderInput('message', editedCake.message) : editedCake.message}
                        />
                        <ThermalReceiptRow
                            label="Color"
                            value={isEditing ? renderInput('color', editedCake.color) : editedCake.color}
                        />
                    </ThermalReceiptSection>
                    <ThermalReceiptSection title="Notas">
                        {(editedCake.comments !== '' || isEditing) && (
                            <ThermalReceiptRow
                                label="Comentarios"
                                value={isEditing ? renderInput('comments', editedCake.comments) : editedCake.comments}
                            />
                        )}
                        <ThermalReceiptRow
                            label="Pagado"
                            value={editedCake.prePaid ? 'Si' : 'No'}
                            emphasize={editedCake.prePaid}
                        />
                        <ThermalReceiptRow
                            label="Cajer@"
                            value={isEditing ? renderInput('cashier', editedCake.cashier) : editedCake.cashier}
                        />
                    </ThermalReceiptSection>
                </ThermalReceipt>
            </PrintContainer>
            <div>
                {isEditing ? (
                    <button onClick={saveChanges}>Guardar</button>
                ) : (
                    <button onClick={() => setIsEditing(true)}>Editar</button>
                )}
                <Link to={`/topper/${cake.id}`}>
                    <button>🎨 Diseñar Topper</button>
                </Link>
                <button onClick={deleteCakeOrder} style={{ backgroundColor: 'red' }}>BORRAR</button>
            </div>
            <OrderAuditTimeline auditLog={auditLog} />
        </div>
    );
}
